import React from 'react';
import { motion } from 'framer-motion';
import '../../../css/pages.css';

// Motion component aliases
const MotionDiv = motion.div;
const MotionH2 = motion.h2;
const MotionP = motion.p;

const Features = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 40, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { scale: 0.95, opacity: 0, y: 20 },
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const features = [
    {
      id: 1,
      icon: "bi bi-graph-up-arrow",
      title: "AI Sales Insights",
      description: "Stay ahead of your business with real-time sales analytics. View your top-performing products and get AI-powered recommendations to optimize your sales and boost profitability.",
      gradient: "linear-gradient(135deg, #ff6b9d, #c471ed)",
      size: "large"
    },
    {
      id: 2,
      icon: "bi bi-lightning-charge",
      title: "Lightning-Fast Orders",
      description: "Track your transaction flow and spot areas for improvement. Monitor sales and assignments in real-time, and see how your business evolves with seamless order processing.",
      gradient: "linear-gradient(135deg, #12c2e9, #00d4ff)",
      size: "medium"
    },
    {
      id: 3,
      icon: "bi bi-translate",
      title: "Multi-Language Support",
      description: "Serve customers in their preferred language with built-in multi-language support. Switch between English, Mandarin, Malay, and Tamil effortlessly for better customer experience.",
      gradient: "linear-gradient(135deg, #8b00ff, #ff0080)",
      size: "medium"
    },
    {
      id: 4,
      icon: "bi bi-robot",
      title: "Smart Inventory & Trends",
      description: "Focus on what matters most – AI helps you identify top-selling products and inventory shortages, providing actionable insights for smarter decisions. Never run out of popular items again.",
      gradient: "linear-gradient(135deg, #ff9a9e, #fecfef)",
      size: "medium"
    },
    {
      id: 5,
      icon: "bi bi-moon-stars",
      title: "Night & Dark Mode",
      description: "Protect your eyes during long working hours with our elegant dark mode interface. Automatically adjusts to ambient lighting conditions for optimal visibility and reduced eye strain.",
      gradient: "linear-gradient(135deg, #667eea, #764ba2)",
      size: "medium"
    },
    {
      id: 6,
      icon: "bi bi-palette",
      title: "Beautiful & Simple POS",
      description: "Experience intuitive design that makes complex operations feel effortless. Clean interface, logical workflows, and stunning visuals that make work enjoyable for you and your staff.",
      gradient: "linear-gradient(135deg, #f093fb, #f5576c)",
      size: "large"
    }
  ];

  return (
    <div className="features">
      <MotionDiv 
        className="features-container"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        {/* Title Section */}
        <div className="section-title">
          <MotionH2 variants={itemVariants}>
            Revolutionary Features
          </MotionH2>
          <MotionDiv 
            className="subtitle-wrapper"
            variants={itemVariants}
          >
            <h1 className="features-subtitle">
              POS headaches?<br />
              Simplify and sell smarter!
            </h1>
          </MotionDiv>
          <MotionP variants={itemVariants}>
            Discover how AI transforms traditional hawker operations into modern, efficient businesses
          </MotionP>
        </div>

        <MotionH2 
          className="features-tagline"
          variants={itemVariants}
        >
          Unlock features that turn your POS struggles into wins, and watch your business thrive!
        </MotionH2>

        {/* Key Features Section */}
        <MotionDiv className="key-features">
          <MotionH2 
            className="key-features-title"
            variants={itemVariants}
          >
            Key Features
          </MotionH2>

          <div className="features-grid-new">
            {features.map((feature, index) => (
              <MotionDiv 
                key={feature.id}
                className={`feature-card-new ${feature.size}`}
                variants={cardVariants}
                whileHover={{ 
                  y: -8,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                style={{ 
                  '--gradient': feature.gradient,
                  '--delay': `${index * 0.1}s`
                }}
              >
                <div className="feature-icon-new">
                  <i className={feature.icon}></i>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </MotionDiv>
            ))}
          </div>
        </MotionDiv>
      </MotionDiv>
    </div>
  );
};

export default Features;